import type { GameId } from '@casino/shared';

/**
 * Emblemes des jeux du menu.
 *
 * Les cartes se contentaient d'un motif CSS decoratif, ce qui faisait pauvre.
 * Chaque jeu a desormais un embleme dessine, dans la meme grammaire que
 * l'embleme principal de `public/brand/` : trait fin, un seul accent laiton,
 * aucune ombre portee.
 *
 * Construits par primitives typees plutot que par `innerHTML` : le contenu est
 * statique et connu a la compilation, autant ne jamais ouvrir ce chemin-la.
 *
 * Le trait suit `currentColor`, donc la carte controle la couleur au survol et
 * a l'etat desactive. Seul l'accent reste laiton en toutes circonstances.
 */

const NS = 'http://www.w3.org/2000/svg';
const BRASS = '#c9a961';

/** `line` : trait qui suit la couleur du texte. `accent` : aplat laiton. */
type Role = 'line' | 'accent';

type Primitive =
  | { readonly kind: 'path'; readonly d: string; readonly role: Role; readonly transform?: string }
  | {
      readonly kind: 'rect';
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
      readonly rx: number;
      readonly role: Role;
      readonly transform?: string;
    }
  | {
      readonly kind: 'circle';
      readonly cx: number;
      readonly cy: number;
      readonly r: number;
      readonly role: Role;
    }
  | {
      readonly kind: 'ellipse';
      readonly cx: number;
      readonly cy: number;
      readonly rx: number;
      readonly ry: number;
      readonly role: Role;
    };

/** Pique de la charte, en coordonnees 0-100, reutilise a plusieurs echelles. */
const SPADE =
  'M50 14 C50 14 20 40 20 57 C20 68 28 75.5 37 75.5 C43 75.5 47 72.5 49 68.5 ' +
  'C48 78.5 44 86.5 37.5 90.5 L62.5 90.5 C56 86.5 52 78.5 51 68.5 ' +
  'C53 72.5 57 75.5 63 75.5 C72 75.5 80 68 80 57 C80 40 50 14 50 14 Z';

/** Place le pique a une position et une taille donnees. */
function spadeAt(x: number, y: number, scale: number): string {
  return `translate(${x} ${y}) scale(${scale}) translate(-50 -52)`;
}

const EMBLEMS: Readonly<Record<GameId, readonly Primitive[]>> = {
  // Cabinet, trois fenetres, un levier.
  slots: [
    { kind: 'rect', x: 9, y: 9, w: 40, h: 46, rx: 6, role: 'line' },
    { kind: 'rect', x: 15, y: 17, w: 28, h: 17, rx: 3, role: 'line' },
    { kind: 'path', d: 'M24.3 17v17 M33.7 17v17', role: 'line' },
    { kind: 'circle', cx: 19.7, cy: 25.5, r: 2.6, role: 'accent' },
    { kind: 'circle', cx: 29, cy: 25.5, r: 2.6, role: 'accent' },
    { kind: 'circle', cx: 38.3, cy: 25.5, r: 2.6, role: 'accent' },
    // Fente de paiement.
    { kind: 'rect', x: 19, y: 43, w: 20, h: 4, rx: 2, role: 'line' },
    // Levier : bras puis pommeau.
    { kind: 'path', d: 'M49 24 L55 18', role: 'line' },
    { kind: 'circle', cx: 56.5, cy: 16, r: 3.2, role: 'accent' },
  ],

  // Deux cartes en eventail, pique sur celle de devant.
  blackjack: [
    {
      kind: 'rect',
      x: 12,
      y: 15,
      w: 25,
      h: 35,
      rx: 3.5,
      role: 'line',
      transform: 'rotate(-17 24.5 32.5)',
    },
    { kind: 'rect', x: 27, y: 15, w: 25, h: 35, rx: 3.5, role: 'line' },
    { kind: 'path', d: SPADE, role: 'accent', transform: spadeAt(39.5, 32.5, 0.19) },
  ],

  // Roue a rayons, moyeu et bille.
  roulette: [
    { kind: 'circle', cx: 32, cy: 32, r: 24, role: 'line' },
    { kind: 'circle', cx: 32, cy: 32, r: 15, role: 'line' },
    {
      kind: 'path',
      d:
        'M47 32L56 32 M42.6 42.6L49 49 M32 47L32 56 M21.4 42.6L15 49 ' +
        'M17 32L8 32 M21.4 21.4L15 15 M32 17L32 8 M42.6 21.4L49 15',
      role: 'line',
    },
    { kind: 'circle', cx: 32, cy: 32, r: 4.2, role: 'accent' },
    // La bille, calee dans le chemin de roulement.
    { kind: 'circle', cx: 41.8, cy: 15.1, r: 2.8, role: 'accent' },
  ],

  // Pile de jetons, une carte glissee derriere.
  poker: [
    {
      kind: 'rect',
      x: 33,
      y: 8,
      w: 21,
      h: 29,
      rx: 3,
      role: 'line',
      transform: 'rotate(14 43.5 22.5)',
    },
    { kind: 'ellipse', cx: 26, cy: 47, rx: 16, ry: 6, role: 'line' },
    { kind: 'ellipse', cx: 26, cy: 40, rx: 16, ry: 6, role: 'line' },
    { kind: 'ellipse', cx: 26, cy: 33, rx: 16, ry: 6, role: 'line' },
    // Les flancs de la pile, pour qu'elle ait une epaisseur.
    { kind: 'path', d: 'M10 33v14 M42 33v14', role: 'line' },
    { kind: 'ellipse', cx: 26, cy: 33, rx: 7.5, ry: 2.8, role: 'accent' },
  ],
};

function applyRole(node: SVGElement, role: Role): void {
  if (role === 'accent') {
    node.setAttribute('fill', BRASS);
    node.setAttribute('stroke', 'none');
    return;
  }
  node.setAttribute('fill', 'none');
  node.setAttribute('stroke', 'currentColor');
  node.setAttribute('stroke-width', '1.7');
  node.setAttribute('stroke-linecap', 'round');
  node.setAttribute('stroke-linejoin', 'round');
}

function build(primitive: Primitive): SVGElement {
  switch (primitive.kind) {
    case 'path': {
      const node = document.createElementNS(NS, 'path');
      node.setAttribute('d', primitive.d);
      if (primitive.transform !== undefined) node.setAttribute('transform', primitive.transform);
      applyRole(node, primitive.role);
      return node;
    }
    case 'rect': {
      const node = document.createElementNS(NS, 'rect');
      node.setAttribute('x', String(primitive.x));
      node.setAttribute('y', String(primitive.y));
      node.setAttribute('width', String(primitive.w));
      node.setAttribute('height', String(primitive.h));
      node.setAttribute('rx', String(primitive.rx));
      if (primitive.transform !== undefined) node.setAttribute('transform', primitive.transform);
      applyRole(node, primitive.role);
      return node;
    }
    case 'circle': {
      const node = document.createElementNS(NS, 'circle');
      node.setAttribute('cx', String(primitive.cx));
      node.setAttribute('cy', String(primitive.cy));
      node.setAttribute('r', String(primitive.r));
      applyRole(node, primitive.role);
      return node;
    }
    case 'ellipse': {
      const node = document.createElementNS(NS, 'ellipse');
      node.setAttribute('cx', String(primitive.cx));
      node.setAttribute('cy', String(primitive.cy));
      node.setAttribute('rx', String(primitive.rx));
      node.setAttribute('ry', String(primitive.ry));
      applyRole(node, primitive.role);
      return node;
    }
  }
}

/** Embleme d'un jeu, decoratif : le nom du jeu est deja dans le texte de la carte. */
export function gameEmblem(id: GameId, options: { readonly size?: number } = {}): SVGSVGElement {
  const size = options.size ?? 56;
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 64 64');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.classList.add('game-emblem');

  for (const primitive of EMBLEMS[id]) svg.appendChild(build(primitive));
  return svg;
}
